/* eslint-disable complexity */
import { BoxType, CosmeticRarity, CosmeticType, currencyName, HeroStatus, NftItemRarity, NftRefType, NftSkinItemRarity, NtfHeroClassType } from '@wolffun/shared/src/types';
import { createSelectorHook, useDispatch } from 'react-redux';
import { BalanceType, CosmeticSource, HeroSource, VestingSafeType, BoxSource } from '../models';
import { HeroTrophy, RentalState, UserStatus } from '../utils/types';
import { CurrencyType, serverCurrencyToSystemCurrency } from '@wolffun/system-currency';

/** Typed selector */
export const useTypedSelector = createSelectorHook();
/** Typed dispatcher */

export const useTypedDispatch = useDispatch;
export const parseUser = user => {
  const {
    address,
    avatarId,
    canClaimFreeHero,
    country,
    created,
    email,
    frameId,
    id,
    lastModified,
    nonce,
    numChangeName,
    playerStatistic,
    status,
    username,
    userProfile,
    version,
    isCreatorProgram
  } = user || {};
  const {
    battle,
    hero,
    mega,
    mvp,
    streak,
    triple,
    victory
  } = playerStatistic || {};
  const {
    level,
    levelUpGPP,
    xp
  } = userProfile || {};
  return {
    address: typeof address === 'string' ? address : '',
    avatarId: typeof avatarId === 'number' ? avatarId : 0,
    canClaimFreeHero: typeof canClaimFreeHero === 'boolean' ? canClaimFreeHero : false,
    country: typeof country === 'string' ? country : '',
    created: typeof created === 'string' && !isNaN(Date.parse(created)) ? Date.parse(created) : 0,
    email: typeof email === 'string' ? email : '',
    frameId: typeof frameId === 'number' ? frameId : 0,
    id: typeof id === 'string' ? id : '',
    lastModified: typeof lastModified === 'string' && !isNaN(Date.parse(lastModified)) ? Date.parse(lastModified) : 0,
    nonce: typeof nonce === 'number' ? nonce : 0,
    numChangeName: typeof numChangeName === 'number' ? numChangeName : 0,
    playerStatistic: {
      battle: typeof battle === 'number' ? battle : 0,
      hero: typeof hero === 'number' ? hero : 0,
      mega: typeof mega === 'number' ? mega : 0,
      mvp: typeof mvp === 'number' ? mvp : 0,
      streak: typeof streak === 'number' ? streak : 0,
      triple: typeof triple === 'number' ? triple : 0,
      victory: typeof victory === 'number' ? victory : 0
    },
    status: typeof status === 'number' ? status : UserStatus.Active,
    username: typeof username === 'string' ? username : '',
    userProfile: {
      level: typeof level === 'number' ? level : 0,
      levelUpGPP: typeof levelUpGPP === 'number' ? levelUpGPP : 0,
      xp: typeof xp === 'number' ? xp : 0
    },
    version: typeof version === 'number' ? version : 0,
    isCreatorProgram: typeof isCreatorProgram === 'boolean' ? isCreatorProgram : false
  };
};
export const parseAvatarConfigs = avatarConfig => {
  return avatarConfig && Array.isArray(avatarConfig) ? avatarConfig.map(config => parseAvatarData(config)) : undefined;
};
export const parseAvatarData = avatarData => {
  const {
    avatarId,
    avatarRarity,
    imageAvatar,
    name,
    sellType
  } = avatarData || {};
  return {
    avatarId: typeof avatarId === 'number' ? avatarId : 1,
    avatarRarity: typeof avatarRarity === 'number' ? avatarRarity : 0,
    imageAvatar: typeof imageAvatar === 'string' ? imageAvatar : '',
    name: typeof name === 'string' ? name : '',
    sellType: typeof sellType === 'number' ? sellType : 0
  };
};
export const parseAvatarAllowedConfigs = avatars => {
  return avatars && Array.isArray(avatars) ? avatars.map(config => parseAvatarAllowedData(config)) : undefined;
};
export const parseAvatarAllowedData = avatarAllowedData => {
  const {
    itemId,
    itemType,
    newItem
  } = avatarAllowedData || {};
  return {
    itemId: typeof itemId === 'number' ? itemId : 0,
    itemType: typeof itemType === 'number' ? itemType : 0,
    newItem: typeof newItem === 'boolean' ? newItem : false
  };
};

const parseCosmeticInfo = (configData, imgPathPattern, keyPath) => {
  const {
    typeId,
    name,
    type,
    rarity,
    isInitial,
    onMarket,
    onNonNft
  } = configData || {};
  return {
    typeId: typeof typeId === 'number' ? typeId : 0,
    name: typeof name === 'string' ? name : '',
    rarity: typeof rarity === 'number' ? rarity : CosmeticRarity.COMMON,
    type: typeof type === 'number' ? type : CosmeticType.AVATAR,
    isInitial: typeof isInitial === 'boolean' ? isInitial : false,
    urlImage: typeof imgPathPattern === 'string' ? String(imgPathPattern).replace(String(keyPath), String(typeId)) : '',
    onNonNFT: typeof onNonNft === 'boolean' ? onNonNft : false,
    onMarket: typeof onMarket === 'boolean' ? onMarket : false
  };
};

export const parseCosmeticConfigs = (data, imgPathPattern, keyPath) => {
  if (data) {
    return Object.keys(data).reduce((acc, key) => {
      acc[Number(key)] = parseCosmeticInfo(data[key], imgPathPattern, keyPath);
      return acc;
    }, {});
  }

  return undefined;
};
export const parseSelectedCosmeticsItems = selectedItems => {
  if (selectedItems && Array.isArray(selectedItems)) {
    return selectedItems.map(item => {
      return typeof item === 'string' ? item : '';
    });
  }

  return [];
};
export const parseSelectedCosmeticsList = data => {
  const {
    selected
  } = data || {};
  return {
    [CosmeticType.AVATAR]: !!selected && Array.isArray(selected[CosmeticType.AVATAR]) ? parseSelectedCosmeticsItems(selected[CosmeticType.AVATAR]) : [],
    [CosmeticType.AVATAR_FRAME]: !!selected && Array.isArray(selected[CosmeticType.AVATAR_FRAME]) ? parseSelectedCosmeticsItems(selected[CosmeticType.AVATAR_FRAME]) : [],
    [CosmeticType.EMOTE]: !!selected && Array.isArray(selected[CosmeticType.EMOTE]) ? parseSelectedCosmeticsItems(selected[CosmeticType.EMOTE]) : [],
    [CosmeticType.SPACESHIP]: !!selected && Array.isArray(selected[CosmeticType.SPACESHIP]) ? parseSelectedCosmeticsItems(selected[CosmeticType.SPACESHIP]) : []
  };
};
export const parseServerCurrency = systemCurrency => {
  return {
    type: systemCurrency.token,
    value: Number(systemCurrency.value),
    decimals: 8,
    name: currencyName[systemCurrency.token]
  };
};
export const parseCosmeticDetail = cosmetic => {
  const {
    id,
    created,
    lastModified,
    userId,
    userAddress,
    tokenId,
    cosmeticInfo,
    cosmeticRarity,
    cosmeticType,
    cosmeticName,
    sale: saleInfo,
    nftId,
    blockchainId
  } = cosmetic;
  return {
    id: typeof id === 'string' ? id : '',
    created: typeof created === 'string' ? created : '',
    lastModified: typeof lastModified === 'string' ? lastModified : '',
    userId: typeof userId === 'string' ? userId : '',
    userAddress: typeof userAddress === 'string' ? userAddress : '',
    nftId: typeof nftId === 'string' ? nftId : '',
    tokenId: typeof tokenId === 'number' ? tokenId : 0,
    cosmeticInfo: cosmeticInfo,
    saleInfo: {
      price: saleInfo !== null && saleInfo !== void 0 && saleInfo.price && saleInfo !== null && saleInfo !== void 0 && saleInfo.price.type ? serverCurrencyToSystemCurrency(saleInfo === null || saleInfo === void 0 ? void 0 : saleInfo.price) : {
        token: CurrencyType.THC,
        value: ''
      }
    },
    cosmeticRarity: typeof cosmeticRarity === 'number' ? cosmeticRarity : CosmeticRarity.COMMON,
    cosmeticType: cosmeticType,
    cosmeticName: typeof cosmeticName === 'string' ? cosmeticName : '',
    blockchainId: typeof blockchainId === 'number' ? blockchainId : 0
  };
};
export const parseBalanceHistory = transaction => {
  const {
    id,
    timestamp,
    changedBalance,
    source
  } = transaction || {};
  return {
    id: typeof id === 'string' ? id : '',
    // API return id property as string, so this field needs to be updated
    timestamp: typeof timestamp === 'number' ? timestamp : 0,
    changedBalance: serverCurrencyToSystemCurrency(changedBalance),
    type: typeof source === 'number' ? source : BalanceType.CLAIM
  };
};
export const parseBoxDataList = boxDataArr => {
  return boxDataArr ? Object.keys(boxDataArr).map(key => {
    const {
      amount,
      processing,
      openTime
    } = boxDataArr[key] || {};
    return {
      boxType: Number(key),
      amount: typeof amount === 'number' ? amount : 0,
      openTime: typeof openTime === 'number' ? openTime * 1000 : undefined,
      processing: typeof processing === 'number' ? processing : 0
    };
  }) : [];
};
export const parseCosmeticHistory = data => {
  const {
    id,
    timestamp,
    itemId,
    source,
    tradeInfo,
    typeId,
    type
  } = data || {};
  return {
    id: typeof id === 'string' ? id : '',
    timestamp: typeof timestamp === 'number' ? timestamp : 0,
    itemId: typeof itemId === 'string' ? itemId : '',
    source: typeof source === 'number' ? source : CosmeticSource.OPEN_BOX,
    typeId: typeof typeId === 'number' ? typeId : 0,
    type: typeof type === 'number' ? type : CosmeticType.AVATAR,
    tradeInfo: tradeInfo ? {
      price: serverCurrencyToSystemCurrency(tradeInfo.price),
      address: typeof tradeInfo.address === 'string' ? tradeInfo.address : ''
    } : undefined
  };
};
export const parseHeroHistory = data => {
  const {
    id,
    timestamp,
    heroId,
    source,
    tradeInfo,
    skinId,
    status
  } = data || {};
  return {
    id: typeof id === 'string' ? id : '',
    timestamp: typeof timestamp === 'number' ? timestamp : 0,
    heroId: typeof heroId === 'string' ? heroId : '',
    skinId: typeof skinId === 'number' ? skinId : 0,
    status: typeof status === 'number' ? status : 0,
    source: typeof source === 'number' ? source : HeroSource.FIRST_FEE,
    tradeInfo: tradeInfo ? {
      price: serverCurrencyToSystemCurrency(tradeInfo.price),
      address: typeof tradeInfo.address === 'string' ? tradeInfo.address : ''
    } : undefined
  };
};
export const parseRentalHistory = data => {
  const {
    id,
    lastModified,
    rentBattles,
    renterAddress,
    skinId,
    state,
    tokenName,
    tokenId,
    refId
  } = data || {};
  return {
    id: typeof id === 'string' ? id : '',
    lastModified: typeof lastModified === 'number' ? lastModified : 0,
    rentBattles: typeof rentBattles === 'number' ? rentBattles : 0,
    renterAddress: typeof renterAddress === 'string' ? renterAddress : '',
    skinId: typeof skinId === 'number' ? skinId : 0,
    state: typeof state === 'number' ? state : RentalState.OWNER_FOR_RENT,
    tokenName: typeof tokenName === 'string' ? tokenName : '',
    tokenId: typeof tokenId === 'string' ? tokenId : '',
    refId: typeof refId === 'string' ? refId : ''
  };
};
export const parseVestingSafeHistory = vestingSafeHistory => {
  const {
    id,
    userId,
    amount,
    balance,
    fee,
    created,
    type,
    source
  } = vestingSafeHistory || {};
  return {
    id: typeof id === 'string' ? id : '',
    userId: typeof userId === 'string' ? userId : '',
    created: typeof created === 'string' ? created : '',
    amount: amount ? serverCurrencyToSystemCurrency(amount) : {
      token: CurrencyType.GTHG,
      value: '0'
    },
    balance: balance ? serverCurrencyToSystemCurrency(balance) : {
      token: CurrencyType.GTHG,
      value: '0'
    },
    fee: fee ? serverCurrencyToSystemCurrency(fee) : {
      token: CurrencyType.GTHG,
      value: '0'
    },
    type: typeof type === 'number' ? type : VestingSafeType.CLAIM,
    source: typeof source === 'number' ? source : null
  };
};
export const parseBattleRewards = data => {
  const {
    matchid,
    result,
    powerPoint,
    thetanCoin,
    expchange,
    timestamp,
    ingamemode,
    rank
  } = data || {};
  return {
    battleId: typeof matchid === 'string' ? matchid : '',
    mode: typeof ingamemode === 'number' ? ingamemode : 0,
    result: typeof result === 'number' ? result : 0,
    rank: typeof rank === 'number' ? rank : 0,
    rewards: {
      gPP: typeof powerPoint === 'number' ? powerPoint : 0,
      gTHC: typeof thetanCoin === 'number' ? thetanCoin : 0,
      exp: typeof expchange === 'number' ? expchange : 0
    },
    timestamp: typeof timestamp === 'number' ? timestamp : 0
  };
};
export const parseNftHeroInventory = hero => {
  const {
    id,
    ownerId,
    tokenId,
    refId,
    refType,
    sale,
    heroTypeId,
    heroRarity,
    skinId,
    skinRarity,
    status,
    marketStatus,
    level,
    imageAvatar,
    imageFull,
    heroRanking,
    heroInfo,
    rarity,
    ownerAddress,
    thcBonus,
    dailyTHCBattleConfig
  } = hero;
  return {
    id: typeof id === 'string' ? id : '',
    ownerId: typeof ownerId === 'string' ? ownerId : '',
    tokenId: typeof tokenId === 'string' ? tokenId : '',
    refId: typeof refId === 'string' ? refId : '',
    refType: typeof refType === 'string' ? refType : NftRefType.HERO,
    systemPrice: sale !== null && sale !== void 0 && sale.price ? serverCurrencyToSystemCurrency(sale === null || sale === void 0 ? void 0 : sale.price) : undefined,
    hp: typeof (heroInfo === null || heroInfo === void 0 ? void 0 : heroInfo.hp) === 'number' ? heroInfo === null || heroInfo === void 0 ? void 0 : heroInfo.hp : 0,
    dmg: typeof (heroInfo === null || heroInfo === void 0 ? void 0 : heroInfo.dmg) === 'number' ? heroInfo === null || heroInfo === void 0 ? void 0 : heroInfo.dmg : 0,
    heroTypeId: typeof heroTypeId === 'number' ? heroTypeId : 0,
    heroRarity: typeof heroRarity === 'number' ? heroRarity : NftItemRarity.COMMON,
    skinId: typeof skinId === 'number' ? skinId : 0,
    skinRarity: typeof skinRarity === 'number' ? skinRarity : NftSkinItemRarity.NORMAL,
    status: typeof status === 'number' ? status : HeroStatus.DEFAULT,
    marketStatus: typeof marketStatus === 'number' ? marketStatus : 0,
    level: typeof level === 'number' ? level : 0,
    trophy: typeof (heroRanking === null || heroRanking === void 0 ? void 0 : heroRanking.trophyClass) === 'number' ? heroRanking === null || heroRanking === void 0 ? void 0 : heroRanking.trophyClass : HeroTrophy.H,
    imageAvatar: typeof imageAvatar === 'string' ? imageAvatar : '',
    imageFull: typeof imageFull === 'string' ? imageFull : '',
    name: typeof (heroInfo === null || heroInfo === void 0 ? void 0 : heroInfo.name) === 'string' ? heroInfo === null || heroInfo === void 0 ? void 0 : heroInfo.name : '',
    rarity: typeof rarity === 'number' ? rarity : NftItemRarity.COMMON,
    heroRole: typeof (heroInfo === null || heroInfo === void 0 ? void 0 : heroInfo.role) === 'number' ? heroInfo === null || heroInfo === void 0 ? void 0 : heroInfo.role : NtfHeroClassType.TANK,
    ownerAddress: typeof ownerAddress === 'string' ? ownerAddress : '',
    battleCap: typeof (heroRanking === null || heroRanking === void 0 ? void 0 : heroRanking.battleCapTHC) === 'number' ? heroRanking === null || heroRanking === void 0 ? void 0 : heroRanking.battleCapTHC : 0,
    battleCapMax: typeof (heroRanking === null || heroRanking === void 0 ? void 0 : heroRanking.totalBattleCapTHC) === 'number' ? heroRanking === null || heroRanking === void 0 ? void 0 : heroRanking.totalBattleCapTHC : 0,
    thcBonus: typeof thcBonus === 'number' ? thcBonus : 0,
    dailyTHCBattleConfig: typeof dailyTHCBattleConfig === 'number' ? dailyTHCBattleConfig : 0
  };
};
export const parseSkinConfigEntities = skinConfigs => {
  if (skinConfigs && Array.isArray(skinConfigs.skinConfigs)) {
    const result = skinConfigs.skinConfigs.reduce((prev, e) => {
      const {
        id,
        name,
        heroTypeId,
        heroRarity,
        skinRarity,
        imageAvatar,
        imageFull,
        imageSmallAvatar,
        model3D,
        scaleRatio
      } = e;
      const item = {
        id: typeof id === 'number' ? id : 0,
        name: typeof name === 'string' ? name : '',
        heroTypeId: typeof heroTypeId === 'number' ? heroTypeId : 0,
        heroRarity: typeof heroRarity === 'number' ? heroRarity : NftItemRarity.COMMON,
        skinRarity: typeof skinRarity === 'number' ? skinRarity : NftItemRarity.COMMON,
        imageAvatar: typeof imageAvatar === 'string' ? imageAvatar : '',
        imageFull: typeof imageFull === 'string' ? imageFull : '',
        imageSmallAvatar: typeof imageSmallAvatar === 'string' ? imageSmallAvatar : '',
        model3D: typeof model3D === 'string' ? model3D : '',
        scaleRatio: typeof scaleRatio === 'number' ? scaleRatio : 1
      };
      return { ...prev,
        [id]: item
      };
    }, {});
    return result;
  }

  return undefined;
};
export const parseGiftBoxDataList = boxDataArr => {
  return boxDataArr ? Object.keys(boxDataArr).map(key => {
    const {
      amount,
      processing,
      openTime
    } = boxDataArr[key] || {};
    return {
      boxType: Number(key),
      amount: typeof amount === 'number' ? amount : 0,
      openTime: typeof openTime === 'number' ? openTime * 1000 : undefined,
      processing: typeof processing === 'number' ? processing : 0
    };
  }) : [];
};

const isValidCurrencyType = type => {
  return Object.values(CurrencyType).includes(type);
};

export const parseBoxHistory = data => {
  var _boxPurchaseEvent$pri;

  const {
    id,
    timestamp,
    boxType,
    source,
    boxPurchaseEvent
  } = data || {};
  return {
    id: typeof id === 'string' ? id : '',
    timestamp: typeof timestamp === 'number' ? timestamp : 0,
    boxType: typeof boxType === 'number' ? boxType : BoxType.COMMON,
    source: typeof source === 'number' ? source : BoxSource.PURCHASE,
    boxPurchaseEvent: boxPurchaseEvent && isValidCurrencyType(boxPurchaseEvent === null || boxPurchaseEvent === void 0 ? void 0 : (_boxPurchaseEvent$pri = boxPurchaseEvent.price) === null || _boxPurchaseEvent$pri === void 0 ? void 0 : _boxPurchaseEvent$pri.type) ? {
      price: serverCurrencyToSystemCurrency(boxPurchaseEvent.price),
      amount: typeof boxPurchaseEvent.amount === 'number' ? boxPurchaseEvent.amount : 0
    } : undefined
  };
};